import { FeMapPlugin } from '@fe/maplibre-extensions';
import * as turf from '@turf/turf';
import { GeoJSONSource, Map as MaplibreMap } from 'maplibre-gl';
import { AirportSmallDto } from '../airports/airport-dto';

export const DIVERSION_LAYER_ID = 'fe-diversion-airports-layer';
export const DIVERSION_SOURCE_ID = 'fe-diversion-airports';

export interface DiversionAirportProperties {
    faaId: string | null;
    icaoId: string | null;
    iataId: string | null;
    name: string | null;
}

export class DiversionStatusLayer implements FeMapPlugin {
    private map: MaplibreMap | null = null;

    setShow(show: boolean) {
        this.map?.setLayoutProperty(DIVERSION_LAYER_ID, 'visibility', show ? 'visible' : 'none');
    }

    async update(airports: AirportSmallDto[]) {
        const source = this.map?.getSource(DIVERSION_SOURCE_ID) as GeoJSONSource | null;
        if (!source) {
            return;
        }

        const features = airports.filter((x) => x.position?.isValid).map((airport) =>
            turf.point(
                [airport.position.lng, airport.position.lat],
                {
                    faaId: airport.faa,
                    icaoId: airport.icao,
                    iataId: airport.iata,
                    name: airport.name
                } satisfies DiversionAirportProperties,
                {
                    id: airport.id
                }
            )
        );

        source.updateData({ add: features });
    }

    onAdd(map: MaplibreMap) {
        this.map = map;
        if (!map.hasImage('fe-diversion-triangle')) {
            const size = 16;
            const canvas = document.createElement('canvas');
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext('2d')!;

            // Draw triangle
            ctx.beginPath();
            ctx.moveTo(size / 2, 2); // Top point
            ctx.lineTo(2, size - 2); // Bottom left
            ctx.lineTo(size - 2, size - 2); // Bottom right
            ctx.closePath();

            // Add stroke
            ctx.strokeStyle = 'rgba(0,0,0,0.8)';
            ctx.lineWidth = 3;
            ctx.stroke();

            ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
            ctx.lineWidth = 1;
            ctx.stroke();

            const imageData = ctx.getImageData(0, 0, size, size);
            map.addImage('fe-diversion-triangle', imageData);
            canvas.remove();
        }

        map.addSource(DIVERSION_SOURCE_ID, {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: []
            }
        });

        map.addLayer(
            {
                id: DIVERSION_LAYER_ID,
                type: 'symbol',
                source: DIVERSION_SOURCE_ID,
                layout: {
                    'icon-image': 'fe-diversion-triangle',
                    'icon-size': 1,
                    'icon-allow-overlap': true,
                    'icon-offset': [0, -15]
                }
            },
            'fe-diversion-icons'
        );
    }

    onRemove(map: MaplibreMap) {
        map.removeLayer(DIVERSION_LAYER_ID);
        map.removeSource(DIVERSION_SOURCE_ID);
        this.map = null;
    }
}
